import React, { useState } from "react";
import { ChevronDown, ChevronUp, BarChart3, Smartphone } from "lucide-react";
import VersionAnalyticsCard from "./VersionAnalyticsCard";
import AnalyticsTable from "./AnalyticsTable";
import type {
  VersionBreakdown,
  DeviceBreakdown,
} from "src/store/presenceSlice";

interface AnalyticsSectionProps {
  versionBreakdowns: VersionBreakdown[];
  deviceBreakdowns: DeviceBreakdown[];
}

const AnalyticsSection: React.FC<AnalyticsSectionProps> = ({
  versionBreakdowns,
  deviceBreakdowns,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasData = versionBreakdowns.length > 0 || deviceBreakdowns.length > 0;

  if (!hasData) {
    return null;
  }

  return (
    <div className="w-full max-w-6xl mx-auto mt-16">
      {/* Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl border border-white/10 bg-white/5 backdrop-blur-xl hover:bg-white/10 transition-all mb-6"
      >
        <BarChart3 className="w-5 h-5 text-gray-300" />
        <span className="text-lg font-semibold text-white">
          Detailed Analytics
        </span>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-300" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-300" />
        )}
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="space-y-8 animate-in fade-in slide-in-from-top-4 duration-300">
          {/* Version Distribution Section */}
          {versionBreakdowns.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-gray-300" />
                <h3 className="text-xl font-bold text-white">
                  Version Distribution
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {versionBreakdowns.map((breakdown, idx) => (
                  <VersionAnalyticsCard key={idx} breakdown={breakdown} />
                ))}
              </div>
            </div>
          )}

          {/* Device Breakdown Section */}
          {deviceBreakdowns.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Smartphone className="w-5 h-5 text-gray-300" />
                <h3 className="text-xl font-bold text-white">
                  Device Breakdown
                </h3>
              </div>
              <AnalyticsTable devices={deviceBreakdowns} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyticsSection;
